import React, { useMemo, useState } from 'react'
import {
  Virtuoso,
  type VirtuosoProps,
  type VirtuosoHandle,
} from 'react-virtuoso'
import styled, { css } from 'styled-components'

import { mediaMaxTablet } from '../theme'

import { hiddenScrollbarCss } from './hidden-scrollbar'

import type { ClassNameProps, StyleProps } from '@root/shared/react'
import type { Components } from 'react-virtuoso'

export type FadeOverflowVirtuozoProps<ItemData, ContextType> = VirtuosoProps<
  ItemData,
  ContextType
> & {
  virtuosoRef?: React.RefObject<VirtuosoHandle | null>
  blockEnd?: number
  blockStart?: number
} & ClassNameProps &
  StyleProps

export const FadeOverflowVirtuozo = <ItemData, ContextType>({
  virtuosoRef,
  blockEnd: initialBlockEnd = 16,
  blockStart: initialBlockStart = 16,
  className,
  style,
  ...virtuosoProps
}: FadeOverflowVirtuozoProps<ItemData, ContextType>): React.ReactElement => {
  const [blockStart, setBlockStart] = useState(initialBlockStart)
  const [blockEnd, setBlockEnd] = useState(initialBlockEnd)

  const handleAtTopStateChange = (atTop: boolean) =>
    setBlockStart(atTop ? 0 : initialBlockStart)

  const handleAtBottomStateChange = (atBottom: boolean) =>
    setBlockEnd(atBottom ? 0 : initialBlockEnd)

  const hasData = (virtuosoProps.data?.length ?? 0) > 0

  const Header_ = useMemo(() => {
    return () => (hasData ? <Header /> : undefined)
  }, [hasData])

  const Footer_ = useMemo(() => {
    return () => (hasData ? <Footer /> : undefined)
  }, [hasData])

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const components_ = useMemo((): Components<any, any> => {
    return {
      Header: Header_,
      Footer: Footer_,
      ...virtuosoProps.components,
    }
  }, [Header_, Footer_, virtuosoProps.components])

  return (
    <Root
      className={className}
      style={{
        ...style,
        '--mask-start': `${blockStart}px`,
        '--mask-end': `${blockEnd}px`,
      }}
    >
      <VirtuosoStyled
        ref={virtuosoRef}
        {...virtuosoProps}
        atTopStateChange={handleAtTopStateChange}
        atBottomStateChange={handleAtBottomStateChange}
        components={components_}
      />
    </Root>
  )
}

const Root = styled.div`
  overflow: hidden;

  mask-image: linear-gradient(
    to bottom,
    transparent,
    black var(--mask-start),
    black calc(100% - var(--mask-end)),
    transparent
  );
  mask-size: 100%;
  mask-position: 0 0;
  mask-repeat: no-repeat, no-repeat;
`

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const VirtuosoStyled = styled(Virtuoso<any, any>)`
  /*
   * Promote the scroller to its own compositor layer. Without this, mouse-wheel
   * scrolling over a Virtuoso list on Windows/Chromium freezes or gets stuck
   * (trackpad and macOS are unaffected): Virtuoso mutates the DOM while
   * scrolling, and non-composited wheel scrolling runs on the (busy) main
   * thread. Known issue with the documented workaround:
   * https://github.com/petyosi/react-virtuoso/issues/675
   * react-virtuoso itself no longer applies it (verified absent in v4.18.1).
   */
  will-change: transform;
  /*
   * No \`overscroll-behavior: contain\` here. \`contain\` disables scroll chaining
   * to ancestors, so when this list is nested inside a page scroller (e.g. the
   * explore feeds) a mouse wheel at the list's top/bottom edge never scrolls the
   * page on Windows/Chromium. The browser default (\`auto\`) chains correctly,
   * matching the pre-virtualization plain list. If a consumer needs the list to
   * NOT scroll its background (e.g. a modal without body-scroll-lock), scope the
   * containment to that consumer rather than adding it back globally.
   */

  ${mediaMaxTablet(css`
    ${hiddenScrollbarCss}
  `)}
`

const Header = styled.div`
  padding-block-start: var(--overflow-padding-start, 8px);
`
const Footer = styled.div`
  padding-block-end: var(--overflow-padding-end, 8px);
`
